import TableRow from './TableRow.vue'
import TableRender from './TableRender'
import Page from '../../page'

export default {
  name: 'fdd-table',
  components: { TableRow, TableRender, Page },
  provide() {
    return { Table: this }
  },
  props: {
    // 列表数据
    data: Array,

    // 列配置
    columns: {
      type: Array,
      required: true
    },

    // 行数据主键，方便索引
    // 列表选择/展开子行等功能必须指定正确的 rowKey
    rowKey: {
      type: String,
      default: 'id'
    },

    // 过滤信息
    filters: {
      type: Object,
      default: () => ({})
    },

    // 是否本地分页，不经过后端
    localPaging: {
      type: Boolean,
      default: false
    },

    // 分页信息 为 false 时关闭分页
    pager: {
      type: [Boolean, Object],
      default: () => ({
        size: 20, // 每页记录数
        current: 1, // 当前页
        total: 0 // 总记录数
      })
    },

    // 自动加载
    autoLoad: {
      type: Boolean,
      default: true
    },

    // 加载方法
    query: Function,

    // 分页方法
    paged: Function,

    // 无数据提示
    emptyText: {
      type: String,
      default: undefined
    },

    // 默认排序字段
    defaultSort: {
      type: Object,
      default: () => ({
        prop: '', //字段
        order: '' //排序方式 asc 升序 desc 降序
      })
    },

    /**
     * 选择
     * true: 单页多选
     * false: 禁止选择
     * 'multi-page' 跨页多选
     * 'single' 单选
     * */
    selectable: {
      type: [Boolean, String],
      default: true,
      validator(val) {
        return [true, false, 'multi-page', 'single'].includes(val)
      }
    },

    // 检测行是否可选择
    rowSelectable: {
      type: Function,
      default: row => true
    },

    // 展开子行 render
    expand: Function,

    // 初始化行数据
    initRow: Function
  },

  data() {
    return {
      // 是否出现纵向滚动条
      hasScroller: false,

      // 显示的过滤框
      filterPop: false,

      // 加载遮罩
      loading: false,

      // 请求错误
      queryError: '',

      // 加载的数据
      list: [],

      scrollerWidth: 0, // 滚动条宽度

      selectMap: {}, // 多选已选中集合

      selectedRow: null, // 单选选中项

      expandId: '', // 展开的选项

      rowKeyInvalid: false // 跨页选择主键失效警告
    }
  },

  computed: {
    // 已选中项
    selected() {
      // 单选
      if (this.selectable === 'single') {
        return this.selectedRow ? [this.selectedRow] : []
      }

      const selected = Object.values(this.selectMap)

      // 跨页多选
      if (this.selectable === 'multi-page') {
        return selected
      }

      // 单页多选
      return selected.filter(item => this.rows.includes(item))
    },

    // 是否已经全选（当前页）
    isAllSelect() {
      if (!this.selectableRows.length) return false

      return this.selectableRows.every(item => this.isRowSelected(item))
    },

    // 可见列
    cols() {
      const cols = this.columns.filter(item => item.visible !== false)

      if ([true, 'multi-page'].includes(this.selectable)) {
        const firstCol = cols[0]
        if (!(firstCol && firstCol.type === 'checkbox')) {
          cols.unshift({
            type: 'checkbox',
            width: '12px'
          })
        }
      }

      return cols
    },

    // 当前展示行数据
    rows() {
      if (this.localPaging) {
        // 本地分页
        const { pager, data } = this
        let { current, size } = pager
        const total = (pager.total = data.length)
        const totalPage = total / size

        if (current > totalPage) {
          current = totalPage
        } else if (current < 1) {
          current = 1
        }

        let start = (current - 1) * size
        let end = start + size + 1

        return this.data.slice(start, end)
      } else if (this.query) {
        // query 查询
        return this.list
      }

      return this.data
    },

    // 当前可选择行数据
    selectableRows() {
      return this.getSelectableRows()
    }
  },

  watch: {
    data: {
      async handler(val, old) {
        // 数据改变则初始话响应字段
        if (val !== old) {
          this.initData(val)
        }
      },
      immediate: true
    },

    rows: {
      async handler(val, old) {
        if (val !== old) {
          // 监测列表数据改动，宽度适应滚动条
          await this.$nextTick()
          this.adjustScroller()
        }
      },
      immediate: true
    },

    rowKeyInvalid(val) {
      if (val) {
        console.warn('[fdd-table] 跨页选择请通过 `row-key` 指定行数据正确的 Key')
      }
    }
  },

  mounted() {
    // 监听关闭过滤弹框
    document.addEventListener('click', this.hideFilterPop)

    // resize debounce 防抖
    this._onResize = () => {
      clearTimeout(this._resizeTimeout)
      this._resizeTimeout = setTimeout(this.adjustScroller, 200)
    }

    // 窗口大小调整后适应滚动条
    window.addEventListener('resize', this._onResize)

    // 自动加载
    if (this.autoLoad && this.query) {
      this.load()
    }
  },

  destroyed() {
    // 移除监听关闭过滤弹框
    document.removeEventListener('click', this.hideFilterPop)

    // 移除 resize
    window.removeEventListener('resize', this._onResize)

    this._onResize = null
  },

  methods: {
    // 初始化数据
    initData(data) {
      if (!data) return []

      const { selectMap } = this

      // 初始化额外响应字段
      data.forEach((item, index) => {
        const id = this.getRowId(item)

        // 更新选中数据
        if (selectMap[id]) {
          selectMap[id] = item
        }
        this.initRow && this.initRow(item)
      })

      return data
    },

    // 加载数据
    async load() {
      this.loading = true

      // 非分页多选清除选中
      if (this.selectable !== 'multi-page') {
        this.selectMap = {}
      }

      // 单选清除选中
      if (this.selectable === 'single') {
        this.selectedRow = null
      }

      this.query(this)
        .then(data => {
          this.queryError = ''
          const { list, total } = data || {}
          this.list = this.initData(list || [])
          if (this.pager) this.pager.total = +total || 0
        })
        .catch(e => {
          this.queryError = e.message || ''
          this.list = []
          this.pager && (this.pager.total = 0)
        })
        .finally(() => {
          this.loading = false
        })
    },

    // 加载首页
    loadFirstPage() {
      this.pager.current && (this.pager.current = 1)
      this.load()
    },

    // 分页
    paging(current = 1, size) {
      if (this.query) {
        Object.assign(this.pager, { current, size })
        this.load()
      } else {
        this.paged(current, size)
      }
    },

    // 获取行 id
    getRowId(row) {
      let id = row[this.rowKey]

      // 跨页多选无效 rowKey 则警告
      if (!id && this.selectable === 'multi-page') {
        this.rowKeyInvalid = true
      }

      return id || this.rows.indexOf(row)
    },

    // 全选切换
    toggleAll() {
      this.select(this.selectableRows, !this.isAllSelect)
    },

    // 可选择的数据
    getSelectableRows(rows = this.rows) {
      return rows.filter(row => this.rowSelectable(row))
    },

    // 勾选
    select(row, isSelect = true) {
      let rows = Array.isArray(row) ? row : row ? [row] : []

      // 单选
      if (this.selectable === 'single') {
        // 不可选
        if (row && !this.rowSelectable(row)) return false

        const oldRow = this.selectedRow
        const selRow = isSelect ? row : null

        this.selectedRow = selRow
        this.$emit('current-change', selRow, oldRow)
      } else {
        rows = this.getSelectableRows(rows)

        // 不可选
        if (!rows.length) return

        const { selectMap } = this
        rows.forEach(item => {
          const id = this.getRowId(item)
          if (isSelect) {
            this.$set(selectMap, id, item)
          } else {
            this.$delete(selectMap, id)
          }
        })
      }

      this.$emit('selection-change', rows, isSelect, this)
    },

    // 是否选中
    isRowSelected(row) {
      if (this.selectable === 'single') {
        return this.selectedRow === row
      }

      const id = this.getRowId(row)
      return !!this.selectMap[id]
    },

    // 宽度适应滚动条
    adjustScroller() {
      let $tbody = this.$refs.tbody

      if ($tbody) {
        let $wrap = $tbody.parentElement

        this.scrollerWidth = Math.min($wrap.offsetWidth - $tbody.clientWidth, 20)
      } else {
        this.scrollerWidth = 0
      }
    },

    // 关闭过滤弹窗
    hideFilterPop() {
      this.filterPop = false
    },

    // 过滤
    filter(prop, val) {
      let map = this.filters

      if (val === undefined) {
        delete map[prop]
      } else {
        map[prop] = val
      }

      // 触发过滤事件
      this.$emit('filter', prop, val, map)

      if (this.query) this.loadFirstPage()
    },

    /**
     * 点击排序
     * @param prop 当前排序字段
     * @param order 排序规则 asc: 升序, desc： 降序
     */
    sortClick(prop, order) {
      const sort = this.defaultSort

      // 已经是当前排序则清除
      if (sort.prop === prop && sort.order === order) {
        prop = ''
        order = ''
      }
      Object.assign(sort, { prop, order })
      this.loadFirstPage()
    },

    /**
     * 展开子行
     * @param row 行数据
     * @param { Boolean | null } isExpand 是否展开，不传则切换状态
     */
    expandRow(row, isExpand) {
      const id = this.getRowId(row)

      if (typeof isExpand !== 'boolean') {
        isExpand = this.expandId !== id
      }
      this.expandId = isExpand ? id : ''
      this.$emit('expand-change', row, isExpand)
    }
  }
}
